/**
 * Blog filtering utilities — shared between BlogIndex and BlogArchive.
 * Decade is derived from the linked Card's event_date (the event's decade,
 * not the publication date). Card category comes from the linked Card.
 */

/**
 * Build a map of card_id → { category, event_date, decade } from a list of Cards.
 * Used to enrich BlogPost records with their linked card's metadata for filtering.
 */
export function buildCardMap(cards) {
  const map = new Map();
  for (const card of cards || []) {
    if (!card.id) continue;
    const decade = deriveDecade(card.event_date);
    map.set(card.id, {
      category: card.category || null,
      event_date: card.event_date || null,
      decade,
    });
  }
  return map;
}

/**
 * Derive a decade label ("1960s") from an ISO date string.
 * Returns null if the date is missing or unparseable.
 */
export function deriveDecade(dateStr) {
  if (!dateStr) return "The Future";
  const year = new Date(dateStr).getFullYear();
  if (isNaN(year)) return "The Future";
  const decade = Math.floor(year / 10) * 10;
  return `${decade}s`;
}

/**
 * Get the card category for a post, from the card map.
 */
export function getPostCategory(post, cardMap) {
  if (!post.card_id) return null;
  return cardMap.get(post.card_id)?.category || null;
}

/**
 * Get the decade for a post, from the linked card's event_date via the card map.
 */
export function getPostDecade(post, cardMap) {
  if (!post.card_id) return null;
  return cardMap.get(post.card_id)?.decade || null;
}

/**
 * Filter posts by the given criteria. All filters combine (AND logic).
 * Tags is multi-select (a post matches if it has ANY of the selected tags).
 *
 * @param {Array} posts — visible posts (already filtered by isPostVisible)
 * @param {Map} cardMap — card_id → { category, decade }
 * @param {Object} filters — { tags: [], decade: null, category: null, search: "" }
 * @returns {Array} filtered posts
 */
export function filterPosts(posts, cardMap, { tags, decade, category, search }) {
  let result = posts;

  if (tags && tags.length > 0) {
    result = result.filter((p) =>
      (p.tags || []).some((t) => tags.includes(t))
    );
  }

  if (decade) {
    result = result.filter((p) => getPostDecade(p, cardMap) === decade);
  }

  if (category) {
    result = result.filter((p) => getPostCategory(p, cardMap) === category);
  }

  if (search && search.trim()) {
    const q = search.trim().toLowerCase();
    result = result.filter(
      (p) =>
        (p.title || "").toLowerCase().includes(q) ||
        (p.body || "").toLowerCase().includes(q)
    );
  }

  return result;
}

/**
 * Collect all unique tags from visible posts, sorted alphabetically.
 */
export function getAllTags(posts) {
  return [...new Set(posts.flatMap((p) => p.tags || []))].sort();
}

/**
 * Collect all unique decades from visible posts (via linked cards), sorted descending.
 */
export function getAllDecades(posts, cardMap) {
  const decades = new Set();
  for (const p of posts) {
    const d = getPostDecade(p, cardMap);
    if (d) decades.add(d);
  }
  // Sort real decades descending; "The Future" goes last (after the oldest decade)
  const sorted = [...decades]
    .filter((d) => d !== "The Future")
    .sort((a, b) => b.localeCompare(a));
  if (decades.has("The Future")) sorted.push("The Future");
  return sorted;
}

/**
 * Collect all unique card categories from visible posts (via linked cards), sorted alphabetically.
 */
export function getAllCategories(posts, cardMap) {
  const cats = new Set();
  for (const p of posts) {
    const c = getPostCategory(p, cardMap);
    if (c) cats.add(c);
  }
  return [...cats].sort();
}

/**
 * Group posts by publication year (from published_date or created_date).
 * Returns a Map: year (number) → posts[], sorted descending by year.
 */
export function groupByYear(posts) {
  const map = new Map();
  for (const p of posts) {
    const date = p.published_date || p.created_date;
    if (!date) continue;
    const year = new Date(date).getFullYear();
    if (isNaN(year)) continue;
    if (!map.has(year)) map.set(year, []);
    map.get(year).push(p);
  }
  // Sort posts within each year by date descending
  for (const [year, yearPosts] of map) {
    yearPosts.sort(
      (a, b) =>
        new Date(b.published_date || b.created_date) -
        new Date(a.published_date || a.created_date)
    );
  }
  return [...map.entries()].sort((a, b) => b[0] - a[0]);
}